<script lang="ts" setup>
import type { TripMonitorRoute, TripMonitorRouteStop } from "@/api/trips/dto/trip-monitor";
import { computed } from "vue";
import { OrderStopType } from "@/data/order";
import { RouteStatus, RouteStopStatus, TravelOrderCancellationStatusMap } from "@/data/trip";
import { RouteRedispatchState } from "@/enums/trip";
import RouteStopIcon from "@/components/routeStop/RouteStopIcon.vue";

const showActionDialog = defineModel<boolean>("showActionDialog", { default: false });
const showInfoDialog = defineModel<boolean>("showInfoDialog", { default: false });

const props = defineProps<{
  tripId: number;
  routeMonitor: TripMonitorRoute;
  nextRouteStop: TripMonitorRouteStop;
  loading: boolean;
  selectedRoute: TripMonitorRoute | null;
}>();

const emits = defineEmits<{
  (e: "setDialogData", tripId: number, route: TripMonitorRoute): void;
  (e: "update:showActionDialog", value: boolean): void;
  (e: "update:showInfoDialog", value: boolean): void;
}>();

const stopTypeName = computed(() => {
  switch (props.nextRouteStop.type) {
    case OrderStopType.pickup:
      return "Pick up";
    case OrderStopType.delivery:
      return "Delivery";
    case OrderStopType.reload:
      return "Reload";
    default:
      return "";
  }
});

const isPickup = computed(() => {
  return props.nextRouteStop.type === OrderStopType.pickup;
});

const status = computed(() => {
  return props.nextRouteStop.status;
});

const isReadyForCheckIn = computed(() => {
  return status.value === RouteStopStatus.inRoute;
});

const isReadyForLoading = computed(() => {
  return status.value === RouteStopStatus.waitingForLoading && isPickup.value;
});

const isReadyForLoadingVerification = computed(() => {
  return status.value === RouteStopStatus.waitingForVerification && isPickup.value;
});

const isReadyForUnloading = computed(() => {
  return status.value === RouteStopStatus.waitingForLoading && !isPickup.value;
});

const isReadyForUnloadingVerification = computed(() => {
  return status.value === RouteStopStatus.waitingForVerification && !isPickup.value;
});

const isReadyForGoodToGo = computed(() => {
  return status.value === RouteStopStatus.waitingForGoodToGo;
});

const actionLabel = computed(() => {
  if (isReadyForCheckIn.value) {
    return "Check in";
  }

  if (isReadyForLoading.value) {
    return "Load and verify";
  }

  if (isReadyForLoadingVerification.value || isReadyForUnloadingVerification.value) {
    return "Verify";
  }

  if (isReadyForUnloading.value) {
    return "Unload and verify";
  }

  if (isReadyForGoodToGo.value && !props.nextRouteStop.isLast) {
    return "Good to go";
  }

  if (isReadyForGoodToGo.value && props.nextRouteStop.isLast) {
    return "Finish";
  }

  return "Check in";
});

const isCancelled = computed(() => {
  return props.routeMonitor.status === RouteStatus.cancelled;
});

const isPlanned = computed(() => {
  return props.routeMonitor.status === RouteStatus.planned;
});

const actionButtonDisabled = computed(() => {
  return (
    props.routeMonitor.redispatch === RouteRedispatchState.needed ||
    !props.routeMonitor.dispatchConfirmed ||
    isPlanned.value ||
    isCancelled.value
  );
});

const isActionButtonVisible = computed(() => {
  return props.routeMonitor.travelOrders.some(({ status }) =>
    TravelOrderCancellationStatusMap.some(({ id }) => id === status),
  );
});

const isActionButtonLoading = computed(() => {
  return Boolean(props.loading && props.selectedRoute?.id === props.routeMonitor.id && showActionDialog.value);
});

const isInfoButtonLoading = computed(() => {
  return Boolean(props.loading && props.selectedRoute?.id === props.routeMonitor.id && showInfoDialog.value);
});

async function openActionDialog() {
  emits("setDialogData", props.tripId, props.routeMonitor);
  emits("update:showActionDialog", true);
}

async function openViewInfo() {
  emits("setDialogData", props.tripId, props.routeMonitor);
  emits("update:showInfoDialog", true);
}
</script>

<template>
  <div class="d-flex align-center mb-2">
    <route-stop-icon :is-reload="props.nextRouteStop.isReload" :type="props.nextRouteStop.type" />
    <div class="text-blue-grey-darken-1 font-weight-500 ml-2" data-qa="next-stop-type">{{ stopTypeName }}</div>
  </div>

  <router-link
    :to="{ name: 'facilities-edit', params: { id: props.nextRouteStop.facilityId } }"
    class="text-primary font-size-12 mt-2 font-weight-500 text-decoration-none"
    data-qa="next-stop-facility-name"
    target="_blank"
  >
    {{ nextRouteStop.facilityName }}
  </router-link>

  <div class="text-grey-darken-2 font-size-11 mt-2" data-qa="next-stop-address">
    {{ nextRouteStop.facilityAddress }}
  </div>

  <div v-if="!isActionButtonVisible" class="d-flex align-center mt-2">
    <v-btn
      v-if="isReadyForGoodToGo"
      :loading="isInfoButtonLoading"
      class="font-weight-600 mr-2"
      color="primary"
      density="comfortable"
      size="small"
      variant="outlined"
      @click="openViewInfo"
    >
      View info
    </v-btn>

    <v-btn
      :disabled="actionButtonDisabled"
      :loading="isActionButtonLoading"
      class="font-weight-600"
      color="primary"
      density="comfortable"
      size="small"
      variant="flat"
      @click="openActionDialog"
    >
      {{ actionLabel }}
    </v-btn>
  </div>
</template>

<style lang="scss" scoped></style>
